/**
 * useFilteredListCards — search + priority + availability filtering for the list views.
 *
 * Runs before grouping/sorting so every view mode filters identically.
 */
import { useMemo } from 'react';

import type { AvailabilityFilterValue } from '@/components/AvailabilityFilter/AvailabilityFilter';
import type { EnrichedListCard, ListPriority } from '@/types/lists';

export interface ListCardFilters {
  search: string;
  /** Empty means no priority filter. */
  priorities: ListPriority[];
  availability: AvailabilityFilterValue;
}

export function hasActiveListFilters({ search, priorities, availability }: ListCardFilters): boolean {
  return search.trim().length > 0 || priorities.length > 0 || availability !== 'all';
}

export default function useFilteredListCards(cards: EnrichedListCard[], filters: ListCardFilters): EnrichedListCard[] {
  const { search, priorities, availability } = filters;

  return useMemo(() => {
    const query = search.trim().toLowerCase();

    return cards.filter((card) => {
      if (query && !card.name.toLowerCase().includes(query)) return false;
      if (priorities.length > 0 && !priorities.includes(card.priority)) return false;
      if (availability === 'available' && card.available_quantity <= 0) return false;
      if (availability === 'unavailable' && card.available_quantity > 0) return false;
      return true;
    });
  }, [cards, search, priorities, availability]);
}
